/*
 * "Only one product from this collection."
 *
 * SCOPE — deliberately narrow.
 * Only /cart/add.js is ever GATED. Quantity controls — the cart's own, and
 * GoKwik's — are never blocked or altered; GoKwik owns the cart.
 *
 * Two deliberate exceptions to "read-only", both added after QA on 01 Sep 2026:
 *   • Other cart mutations (/cart/change, /cart/update, /cart/clear) are watched
 *     read-only so button and message state refreshes the moment the cart
 *     changes. Before this they only refreshed on add, so raising a quantity in
 *     GoKwik's panel past the threshold left every button disabled and the
 *     "add more to unlock" copy showing until the page was reloaded.
 *   • ONE write: if the cart falls back below the threshold while the unlocked
 *     product is still in it, that product is removed. See revokeLimited().
 *
 * An earlier version guarded quantity clicks inside GoKwik's panel too, but it
 * could not tell which row a "+" belonged to and so blocked every product in
 * the cart. Row identification inside their shadow DOM is not reliable enough
 * to be worth it — the add gate below is where this belongs.
 *
 * TWO LAYERS
 *   1. Buttons — once one product from the collection is in the cart, disable
 *                ADD TO CART on every other card in that collection.
 *   2. Block   — reject /cart/add.js for a second product from the collection.
 *                Covers all twelve of this theme's add-to-cart entry points at
 *                once, including quick-add, search results and carousels.
 *
 * Client-side only: it stops real customers, not devtools.
 *
 * Config comes from snippets/collection-limit-config.liquid.
 * Debug with ddCollectionLimitDebug().
 */
(function () {
  if (window.__ddCollectionLimitInit) return;
  window.__ddCollectionLimitInit = true;

  var CFG = window.ddCollectionLimit;
  if (!CFG || !CFG.variantIds || !CFG.variantIds.length) return;

  var MAX_ITEMS = CFG.maxItems || 1;
  /* Per-product quantity belongs to GoKwik now — it caps each product at 1 in
     the cart, sitewide. maxQuantity: 0 means "no theme-side cap", so a product
     already in the cart can still have its quantity raised from the homepage
     card, the collection card and the cart alike, all agreeing with each other.
     Only the ONE-PRODUCT-FROM-THIS-COLLECTION rule (MAX_ITEMS) stays here. */
  var MAX_QTY = (typeof CFG.maxQuantity === 'number' && CFG.maxQuantity > 0)
    ? CFG.maxQuantity
    : Infinity;
  var THRESHOLD = CFG.threshold || 0;

  var LIMITED = Object.create(null);
  CFG.variantIds.forEach(function (id) { LIMITED[String(id)] = true; });

  /* Variant IDs from this collection currently in the cart. */
  var inCart = [];
  /* Whether the cart (excluding this collection) has reached the threshold. */
  var qualified = THRESHOLD <= 0;
  /* Shortfall in paise while below the threshold. */
  var shortfall = 0;

  var uiTimer = null;
  var stateTimer = null;
  var toastTimer = null;

  function isLimited(id) {
    return !!LIMITED[String(id)];
  }

  /* Read through the shared broker: one /cart.js for every consumer, and it
     invalidates its cache on every mutation, so the add gate still sees a cart
     that is current as of the last write. */
  function getCart() {
    if (window.ddCart && window.ddCart.get) return window.ddCart.get();
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function limitedLines(cart) {
    return (cart.items || []).filter(function (item) {
      return isLimited(item.variant_id) || isLimited(item.id);
    });
  }

  function money(paise) {
    return '₹' + Math.ceil(paise / 100);
  }

  /* Cart value excluding this collection's items. */
  function otherTotal(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      if (isLimited(item.variant_id) || isLimited(item.id)) return sum;
      var line = item.final_line_price;
      if (typeof line !== 'number') line = (item.price || 0) * (item.quantity || 0);
      return sum + line;
    }, 0);
  }

  function qualifies(cart) {
    return THRESHOLD <= 0 || otherTotal(cart) >= THRESHOLD;
  }

  /* ---------------- toast ---------------- */

  function notify(message) {
    document.dispatchEvent(new CustomEvent('collection-limit:blocked', { detail: { message: message } }));

    var el = document.getElementById('dd-collection-limit-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'dd-collection-limit-toast';
      el.setAttribute('role', 'status');
      el.style.cssText = [
        'position:fixed', 'left:50%', 'bottom:24px', 'transform:translateX(-50%)',
        'z-index:2147483647', 'max-width:90vw', 'padding:12px 18px',
        'border-radius:8px', 'background:#1a1a1a', 'color:#fff',
        'font-size:14px', 'font-weight:600', 'line-height:1.4',
        'box-shadow:0 4px 16px rgba(0,0,0,.25)', 'pointer-events:none',
        'opacity:0', 'transition:opacity .2s ease'
      ].join(';');
      document.body.appendChild(el);
    }

    el.textContent = message;
    requestAnimationFrame(function () { el.style.opacity = '1'; });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.style.opacity = '0'; }, 3000);
  }

  /* ---------------- request parsing ---------------- */

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && input.url) return input.url;
    return '';
  }

  function isAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url).split('#')[0]);
  }

  /* Any call that CHANGES the cart. Only add is gated; the rest are watched so
     state stays fresh. */
  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url).split('#')[0]);
  }

  function parseAddBody(body) {
    var out = [];

    function push(id, qty) {
      if (id === undefined || id === null || id === '') return;
      out.push({ id: String(id), quantity: parseInt(qty, 10) || 1 });
    }

    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        push(body.get('id'), body.get('quantity'));
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        push(body.get('id'), body.get('quantity'));
      } else if (typeof body === 'string') {
        if (body.trim().charAt(0) === '{') {
          var json = JSON.parse(body);
          if (json.items && json.items.length) json.items.forEach(function (i) { push(i.id, i.quantity); });
          else push(json.id, json.quantity);
        } else {
          var params = new URLSearchParams(body);
          push(params.get('id'), params.get('quantity'));
        }
      } else if (body && typeof body === 'object') {
        if (body.items && body.items.length) body.items.forEach(function (i) { push(i.id, i.quantity); });
        else push(body.id, body.quantity);
      }
    } catch (e) {}

    return out;
  }

  /* ---------------- retry circuit breaker ---------------- */

  /*
   * A caller that retries a failed add would otherwise spin forever against a
   * faked 422. After a couple of refusals for the same variant, let it through.
   */
  var BLOCK_LIMIT = 2;
  var BLOCK_WINDOW_MS = 10000;
  var MUTE_MS = 30000;
  var blockCounts = Object.create(null);
  var blockMutedUntil = Object.create(null);

  function noteBlock(id) {
    var key = String(id);
    var now = Date.now();
    var rec = blockCounts[key];
    if (!rec || now - rec.first > BLOCK_WINDOW_MS) rec = blockCounts[key] = { first: now, n: 0 };
    rec.n++;
    if (rec.n >= BLOCK_LIMIT) blockMutedUntil[key] = now + MUTE_MS;
  }

  function blockMuted(id) {
    var until = blockMutedUntil[String(id)];
    if (!until) return false;
    if (Date.now() > until) {
      delete blockMutedUntil[String(id)];
      delete blockCounts[String(id)];
      return false;
    }
    return true;
  }

  /* ---------------- add gate ---------------- */

  function allowAdd(body) {
    var adds = parseAddBody(body);
    if (!adds.length) return Promise.resolve(true);

    var limited = adds.filter(function (a) { return isLimited(a.id); });
    if (!limited.length) return Promise.resolve(true);

    if (blockMuted(limited[0].id)) return Promise.resolve(true);

    function deny(verdict) {
      noteBlock(limited[0].id);
      return verdict;
    }

    if (limited.length > MAX_ITEMS) return Promise.resolve(deny({ reason: 'items' }));
    if (limited[0].quantity > MAX_QTY) return Promise.resolve(deny({ reason: 'quantity' }));

    return getCart()
      .then(function (cart) {
        if (!qualifies(cart)) {
          return deny({ reason: 'threshold', short: THRESHOLD - otherTotal(cart) });
        }

        var lines = limitedLines(cart);
        if (!lines.length) return true;

        var same = lines.some(function (l) { return String(l.variant_id) === String(limited[0].id); });
        /* Topping up the same product passes; only a SECOND product is blocked. */
        if (same) return MAX_QTY === Infinity ? true : deny({ reason: 'quantity' });
        return deny({ reason: 'items' });
      })
      .catch(function () { return true; });
  }

  function revokedMessage() {
    var template = CFG.revokedMessage ||
      'Your cart fell below ₹%%THRESHOLD%%, so the unlocked product was removed.';
    return template.replace('%%THRESHOLD%%', String(Math.round(THRESHOLD / 100)));
  }

  function thresholdMessage(short) {
    var template = short > 0
      ? (CFG.belowThresholdMessage || 'Reach ₹%%THRESHOLD%% in your cart to unlock this product.')
      : (CFG.belowThresholdShort || CFG.belowThresholdMessage || '');

    return template
      .replace('%%THRESHOLD%%', String(Math.round(THRESHOLD / 100)))
      .replace('%%AMOUNT%%', money(short > 0 ? short : 0));
  }

  function rejectionMessage(verdict) {
    if (!verdict || verdict === true) return CFG.blockedMessage;
    if (verdict.reason === 'threshold') return thresholdMessage(verdict.short);
    if (verdict.reason === 'quantity') return CFG.quantityMessage || CFG.blockedMessage;
    return CFG.blockedMessage;
  }

  /*
   * Instant block, applied optimistically.
   *
   * scheduleState() is a 300ms debounce plus a /cart.js round trip plus a 150ms
   * UI debounce — comfortably long enough for a shopper to tap a second product
   * before the first one has registered. Record the add locally and repaint
   * straight away; refreshState() reconciles against the real cart right after.
   */
  function noteAdded(body) {
    var changed = false;
    parseAddBody(body).forEach(function (a) {
      if (!isLimited(a.id)) return;
      if (inCart.indexOf(String(a.id)) === -1) {
        inCart.push(String(a.id));
        changed = true;
      }
    });
    if (changed) updateUi();
  }

  /*
   * Reservations — close the double-click race.
   *
   * An add is asynchronous and, on this store, not fast. Between the click and
   * the response, inCart is still empty, so the guard happily lets a SECOND
   * card be clicked; that add then reaches the server, which refuses it — while
   * product-form.js has already painted a quantity onto the card. Reserving the
   * variant at click time shuts the window immediately.
   *
   * A reservation is released when the add resolves either way, and expires on
   * its own if no response ever arrives, so a dropped request cannot leave a
   * product permanently un-addable.
   */
  var RESERVE_TTL = 12000;
  var reserved = Object.create(null);

  function reserve(id) {
    id = String(id);
    if (reserved[id]) return;
    reserved[id] = setTimeout(function () { release(id); }, RESERVE_TTL);
    if (inCart.indexOf(id) === -1) {
      inCart.push(id);
      updateUi();
    }
  }

  function release(id) {
    id = String(id);
    if (!reserved[id]) return;
    clearTimeout(reserved[id]);
    delete reserved[id];
    scheduleState();          /* let the real cart have the last word */
  }

  function releaseFor(body) {
    parseAddBody(body).forEach(function (a) { release(a.id); });
  }

  /* ---------------- interceptors ---------------- */

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = urlOf(input);

      /* Only /cart/add.js is GATED — everything else passes through untouched,
         which is what keeps the cart's own quantity controls working. But a
         quantity change still has to REFRESH our state afterwards, or the
         buttons and the unlock message stay stale until the next page load. */
      if (!isAddUrl(url)) {
        var pass = nativeFetch.call(window, input, init);
        if (isCartMutationUrl(url) && pass && typeof pass.then === 'function') {
          pass.then(function (res) { if (res && res.ok) scheduleState(); }, function () {});
        }
        return pass;
      }

      var opts = init || {};
      var body = opts.body !== undefined ? opts.body : (input && input.body);

      return allowAdd(body).then(function (verdict) {
        if (verdict !== true) {
          releaseFor(body);
          var msg = rejectionMessage(verdict);
          notify(msg);
          return new Response(
            JSON.stringify({ status: 422, message: 'Cart Error', description: msg }),
            { status: 422, headers: { 'Content-Type': 'application/json' } }
          );
        }
        return nativeFetch.call(window, input, init).then(function (res) {
          if (res && res.ok) noteAdded(body);   /* grey the other cards now */
          releaseFor(body);                     /* the add has landed */
          scheduleState();                      /* then reconcile with the cart */
          return res;
        });
      });
    };
  }

  var NativeXHR = window.XMLHttpRequest;
  if (typeof NativeXHR === 'function') {
    var open = NativeXHR.prototype.open;
    var send = NativeXHR.prototype.send;

    NativeXHR.prototype.open = function (method, url) {
      this.__ddUrl = url;
      return open.apply(this, arguments);
    };

    NativeXHR.prototype.send = function (body) {
      var xhr = this;
      var args = arguments;

      if (!isAddUrl(xhr.__ddUrl || '')) {
        if (isCartMutationUrl(xhr.__ddUrl || '')) {
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) scheduleState();
          });
        }
        return send.apply(xhr, args);
      }

      allowAdd(body).then(function (verdict) {
        if (verdict !== true) {
          releaseFor(body);
          notify(rejectionMessage(verdict));
          try { xhr.abort(); } catch (e) {}
          return;
        }
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) noteAdded(body);
          releaseFor(body);
          scheduleState();
        });
        send.apply(xhr, args);
      });
    };
  }

  /* ---------------- cart state (read-only) ---------------- */

  /*
   * Revoke: the cart fell back below the threshold while still holding the
   * unlocked product.
   *
   * The add gate only ever guarded the MOMENT of adding, so a shopper could
   * cross the threshold, take the product, then delete something else and keep
   * it with an unqualified cart. This is the script's only write.
   *
   * It cannot loop: the threshold is measured EXCLUDING this collection's own
   * items, so removing them does not change qualification — the next
   * refreshState() finds inCart empty and does nothing.
   */
  var revoking = false;

  function gokwikCartIsOpen() {
    var host = document.querySelector(HOST_SELECTOR);
    var root = host && host.shadowRoot;
    if (!root) return false;
    var nodes;
    try { nodes = root.querySelectorAll('*'); } catch (e) { return false; }
    for (var i = 0; i < nodes.length; i++) {
      var r = nodes[i].getBoundingClientRect();
      /* While the drawer is shut every node in here measures 0x0. */
      if (r.width > 120 && r.height > 80) return true;
    }
    return false;
  }

  function repaintGokwikCart() {
    if (typeof window.openGokwikSideCart !== 'function') return;
    if (!gokwikCartIsOpen()) return;
    try { window.openGokwikSideCart(); } catch (e) {}
  }

  function revokeLimited(lines) {
    if (revoking || !lines.length) return;
    revoking = true;

    var updates = {};
    lines.forEach(function (l) { updates[String(l.variant_id)] = 0; });

    var post = (typeof nativeFetch === 'function') ? nativeFetch : window.fetch;

    post.call(window, '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) {
        if (!r || !r.ok) return;
        inCart = [];
        updateUi();
        notify(revokedMessage());
        /* Repaint the cart panel and the unlock banner against the new cart. */
        document.dispatchEvent(new CustomEvent('cart:updated'));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'collection-limit' }); } catch (e) {}
        }
        repaintGokwikCart();   /* the drawer will not notice our write otherwise */
      })
      .catch(function () {})
      .then(function () { revoking = false; });
  }

  function refreshState() {
    return getCart()
      .then(function (cart) {
        var lines = limitedLines(cart);
        inCart = lines.map(function (l) { return String(l.variant_id); });
        /* An add still in flight is not in the cart yet — keep holding it. */
        Object.keys(reserved).forEach(function (id) {
          if (inCart.indexOf(id) === -1) inCart.push(id);
        });
        qualified = qualifies(cart);
        shortfall = qualified ? 0 : THRESHOLD - otherTotal(cart);
        scheduleUi();

        /* No longer eligible, but still holding the product — take it back. */
        if (!qualified && lines.length) revokeLimited(lines);
      })
      .catch(function () {});
  }

  function scheduleState() {
    clearTimeout(stateTimer);
    stateTimer = setTimeout(refreshState, 300);
  }

  /* ---------------- button state ---------------- */

  /*
   * Three different add-to-cart markups render this collection's products:
   *
   *   1. Collection page  — snippets/card-product-coll.liquid wraps stepper and
   *      button in .quantity-add-to-cart-btns[data-variant-id]
   *   2. Homepage         — snippets/dd-product-card.liquid uses a bare
   *      <button class="{prefix}-cart" data-id="{variant.id}">
   *   3. Product forms    — snippets/card-product.liquid and the PDP, where the
   *      variant lives in <input name="id">
   *
   * All three are handled so the rule holds wherever the product appears.
   */

  var ADD_BUTTON_SELECTOR =
    'button[name="add"], .quick-add__submit, .product-form__submit, button[class*="cart"][data-id]';

  function buttonsIn(scope) {
    if (scope.matches && scope.matches('button[class*="cart"][data-id]')) return [scope];
    return Array.prototype.slice.call(scope.querySelectorAll(ADD_BUTTON_SELECTOR));
  }

  function limitedTargets() {
    var out = [];

    document.querySelectorAll('.quantity-add-to-cart-btns[data-variant-id]').forEach(function (wrap) {
      var id = wrap.getAttribute('data-variant-id');
      if (isLimited(id)) out.push({ scope: wrap, variantId: String(id) });
    });

    document.querySelectorAll('button[class*="cart"][data-id]').forEach(function (btn) {
      var id = btn.getAttribute('data-id');
      if (isLimited(id)) out.push({ scope: btn, variantId: String(id) });
    });

    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      var input = form.querySelector('[name="id"]');
      if (!input || !isLimited(input.value)) return;
      out.push({ scope: form, variantId: String(input.value) });
    });

    return out;
  }

  /* Why this variant may not be added right now, or true if it may. */
  function verdictFor(variantId) {
    if (!qualified) return { reason: 'threshold', short: shortfall };
    /* Already chosen — MORE OF THE SAME product is fine. GoKwik caps it. */
    if (inCart.indexOf(String(variantId)) !== -1) {
      return MAX_QTY === Infinity ? true : { reason: 'quantity' };
    }
    if (inCart.length >= MAX_ITEMS) return { reason: 'items' };
    return true;
  }

  /*
   * Styled as disabled but NOT given the disabled attribute, deliberately.
   * A disabled button fires no click events, so on a touch device tapping it
   * produced no feedback at all — no tooltip, no toast, nothing. Leaving it
   * clickable lets the capture-phase guard below cancel the add AND surface a
   * toast, while the cart carries the persistent explanation.
   */
  function setDisabled(scope, disabled) {
    buttonsIn(scope).forEach(function (btn) {
      /* Never touch a button the theme disabled for its own reasons
         (sold out, quantity rules) — only manage the ones we marked. */
      if (!disabled && btn.getAttribute('data-dd-limited') !== 'true') return;
      if (disabled && btn.disabled && btn.getAttribute('data-dd-limited') !== 'true') return;

      btn.setAttribute('aria-disabled', String(disabled));
      btn.style.opacity = disabled ? '0.45' : '';
      btn.style.cursor = disabled ? 'not-allowed' : '';

      if (disabled) {
        btn.setAttribute('data-dd-limited', 'true');
      } else {
        btn.removeAttribute('data-dd-limited');
        btn.removeAttribute('title');
      }
    });
  }

  function updateUi() {
    limitedTargets().forEach(function (target) {
      var verdict = verdictFor(target.variantId);

      /* A product already chosen stays enabled so it can still be managed. */
      if (verdict !== true && verdict.reason === 'quantity') {
        setDisabled(target.scope, false);
        return;
      }
      setDisabled(target.scope, verdict !== true);
    });

    updateWidget();
  }

  function scheduleUi() {
    clearTimeout(uiTimer);
    uiTimer = setTimeout(updateUi, 150);
  }

  /* ---------------- floating offer widget ---------------- */

  /*
   * Progress bar for the offer collection's own page. Driven by the same
   * `qualified` / `shortfall` state as the gate and the cart banner, so all
   * three always agree — the scale runs 0 to THRESHOLD rather than a hardcoded
   * figure for exactly that reason.
   *
   * Rendered only when the config says showWidget, which Liquid sets from
   * collection.handle, so it never appears anywhere else.
   */
  var WIDGET_ID = 'dd-offer-widget';
  var WIDGET_STYLE_ID = 'dd-offer-widget-style';

  function ensureWidgetStyle() {
    if (document.getElementById(WIDGET_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = WIDGET_STYLE_ID;
    style.textContent = [
      '#dd-offer-widget{',
      '  position:fixed;left:50%;bottom:50px;transform:translateX(-50%);',
      '  z-index:2147483000;width:calc(100% - 24px);max-width:720px;',
      '  display:flex;align-items:center;gap:14px;',
      '  padding:10px 12px;box-sizing:border-box;',
      '  background:#fff;border:0.8px solid #0E1F4D;border-left:4px solid #0E1F4D;border-radius:14px;',
      '  box-shadow:0 6px 24px rgba(16,24,64,.14);font-family:inherit;',
      '  opacity:0;transition:opacity .25s ease;',
      '}',
      '#dd-offer-widget.is-visible{opacity:1;}',
      '#dd-offer-widget .ddow-icon{',
      '  flex:0 0 auto;width:40px;height:40px;border-radius:10px;background:#eef2ff;',
      '  display:flex;align-items:center;justify-content:center;font-size:19px;line-height:1;',
      '}',
      '#dd-offer-widget .ddow-copy{flex:1 1 auto;min-width:0;}',
      '#dd-offer-widget .ddow-label{',
      '  display:block;font-size:9.5px;font-weight:700;letter-spacing:.09em;',
      '  text-transform:uppercase;color:#8b93a5;margin-bottom:2px;',
      '}',
      '#dd-offer-widget .ddow-text{',
      '  margin:0;font-size:14px;font-weight:700;color:#111827;line-height:1.3;',
      '}',
      '#dd-offer-widget .ddow-text b{color:#e11d2f;font-weight:800;}',
      '#dd-offer-widget .ddow-progress{flex:0 1 210px;min-width:120px;}',
      '#dd-offer-widget .ddow-track{',
      '  position:relative;height:6px;border-radius:999px;background:#eceef3;',
      '}',
      '#dd-offer-widget .ddow-fill{',
      '  height:100%;border-radius:999px;max-width:100%;display: block;',
      '  background:linear-gradient(90deg,#0E1F4D 0%,#365BBF 60%,#3F6CE4 100%);',
      '  transition:width .4s cubic-bezier(.4,0,.2,1);',
      '}',
      '#dd-offer-widget .ddow-knob{',
      '  position:absolute;top:50%;width:14px;height:14px;border-radius:50%;',
      '  background:#fff;border:2px solid #3F6CE4;transform:translate(-50%,-50%);',
      '  box-shadow:0 1px 4px rgba(16,24,64,.28);',
      '  transition:left .4s cubic-bezier(.4,0,.2,1);',
      '}',
      '#dd-offer-widget .ddow-scale{',
      '  display:flex;justify-content:space-between;margin-top:5px;',
      '  font-size:10px;font-weight:600;color:#9aa2b1;',
      '}',
      '#dd-offer-widget .ddow-cta{',
      '  flex:0 0 auto;display:inline-flex;align-items:center;gap:6px;',
      '  padding:10px 16px;border-radius:9px;background:#14224e;color:#fff;',
      '  font-size:13px;font-weight:700;text-decoration:none;white-space:nowrap;',
      '  transition:background .2s ease;',
      '}',
      '#dd-offer-widget .ddow-cta:hover{background:#0E1F4D;}',
      '@media screen and (max-width:749px){',
      '  #dd-offer-widget{flex-wrap:wrap;gap:10px;bottom:12px;}',
      '  #dd-offer-widget .ddow-icon{width:34px;height:34px;font-size:16px;}',
      '  #dd-offer-widget .ddow-text{font-size:12.5px;}',
      '  #dd-offer-widget .ddow-progress{flex:1 1 100%;order:3;}',
      '  #dd-offer-widget .ddow-cta{padding:8px 12px;font-size:12px;}',
      '}'
    ].join('\n');

    document.head.appendChild(style);
  }

  function buildWidget() {
    var el = document.createElement('div');
    el.id = WIDGET_ID;
    el.setAttribute('role', 'status');
    el.innerHTML =
      '<div class="ddow-icon" aria-hidden="true">🎁</div>' +
      '<div class="ddow-copy">' +
        '<span class="ddow-label"></span>' +
        '<p class="ddow-text"></p>' +
      '</div>' +
      '<div class="ddow-progress">' +
        '<div class="ddow-track">' +
          '<div class="ddow-fill" style="width:0%"></div>' +
          '<div class="ddow-knob" style="left:0%"></div>' +
        '</div>' +
        '<div class="ddow-scale"><span class="ddow-min"></span><span class="ddow-max"></span></div>' +
      '</div>' +
      '<a class="ddow-cta" href="' + (CFG.collectionUrl || '#') + '"></a>';

    document.body.appendChild(el);

    /* Already on the offer collection — scroll to the grid, do not reload. */
    el.querySelector('.ddow-cta').addEventListener('click', function (event) {
      var grid = document.getElementById('ProductGridContainer');
      if (!grid) return;
      event.preventDefault();
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

        requestAnimationFrame(function () {
      el.classList.add('is-visible');
      liftAboveStickyBar(el);
    });
    return el;
  }

  /*
   * The PDP sticky add-to-cart sits at bottom:60px and slides in and out on
   * scroll, so measure whichever bar is on screen rather than hardcoding an
   * offset. No bar, no change — the CSS bottom stands.
   */
  function liftAboveStickyBar(el) {
    var bars = document.querySelectorAll('[id^="crowai-v2-bar-"], .crowai-sticky-atc-bar');
    var highest = 0;

    for (var i = 0; i < bars.length; i++) {
      var r = bars[i].getBoundingClientRect();
      if (r.height < 10 || r.bottom < 0) continue;
      if (r.top > window.innerHeight) continue;
      var above = window.innerHeight - r.top;
      if (above > highest) highest = above;
    }

    el.style.bottom = highest > 0 ? (highest + 12) + 'px' : '';
  }

  function updateWidget() {
    if (!CFG.showWidget || THRESHOLD <= 0) return;

    ensureWidgetStyle();
    var el = document.getElementById(WIDGET_ID) || buildWidget();

    var reached = THRESHOLD - shortfall;
    var pct = qualified ? 100 : Math.max(0, Math.min(100, reached * 100 / THRESHOLD));

    el.querySelector('.ddow-label').textContent = CFG.widgetLabel || 'SPECIAL OFFER';
    el.querySelector('.ddow-text').innerHTML = qualified
      ? (CFG.widgetUnlocked || 'Unlocked! Get any item below for <b>₹1</b>')
      : (CFG.widgetLocked || 'Add %%AMOUNT%% more to get any item for <b>₹1</b>')
          .replace('%%AMOUNT%%', money(shortfall));

    el.querySelector('.ddow-fill').style.width = pct + '%';
    el.querySelector('.ddow-knob').style.left = pct + '%';
    el.querySelector('.ddow-min').textContent = money(0);
    el.querySelector('.ddow-max').textContent = money(THRESHOLD);
    liftAboveStickyBar(el);
    el.querySelector('.ddow-cta').textContent = (CFG.widgetCta || 'Explore Offer') + ' ›';
  }

  /* ---------------- click guard ---------------- */

  /*
   * Stop the click before the theme's own handler runs. dd-custom.js shows its
   * own alert('Could not add to cart') on any failed request, so letting the
   * fetch through and faking a 422 would stack a generic browser alert on top
   * of our message. Cancelling here means no request is made at all.
   */
  document.addEventListener('click', function (event) {
    if (!CFG.variantIds.length) return;

    var path = event.composedPath ? event.composedPath() : [event.target];

    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (!el || el.nodeType !== 1 || !el.matches) continue;
      if (!el.matches(ADD_BUTTON_SELECTOR)) continue;

      var variantId = null;

      if (el.matches('button[class*="cart"][data-id]')) {
        variantId = el.getAttribute('data-id');
      } else {
        var wrap = el.closest('.quantity-add-to-cart-btns[data-variant-id]');
        if (wrap) {
          variantId = wrap.getAttribute('data-variant-id');
        } else {
          var form = el.closest('form[action*="/cart/add"]');
          var input = form && form.querySelector('[name="id"]');
          if (input) variantId = input.value;
        }
      }

      if (!variantId || !isLimited(variantId)) return;

      var verdict = verdictFor(variantId);
      if (verdict === true) {
        /* Allowed — reserve it before the add even leaves, so a second card
           cannot be clicked into a refusal while this one is in flight. */
        reserve(variantId);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      notify(rejectionMessage(verdict));
      return;
    }
  }, true);

    /* ---------------- checkout gate ---------------- */

  /*
   * Last theme-side chokepoint before the shopper leaves for checkout. The
   * add-time gate and revokeLimited() cover the obvious paths, but `qualified`
   * can be up to ~300ms stale and revokeLimited() itself needs a round trip —
   * a shopper who drops another item and clicks "Checkout" inside that window
   * would otherwise carry the gift through unqualified. This re-reads the
   * cart FRESH at the moment of the click instead of trusting the cached flag.
   *
   * Same caveat as the add gate: this only fires for clicks that reach the DOM
   * through the normal event system. It cannot stop a direct GET to /checkout,
   * the /cart/<id>:<qty> permalink, an Express Checkout (Shop Pay / Apple Pay
   * / Google Pay) button calling its own SDK, or anything issued outside the
   * browser. Those need the rule enforced in a Cart & Checkout Validation
   * Function — this gate is a courtesy that stops honest shoppers from being
   * surprised at payment, not a security boundary.
   */
  // var CHECKOUT_HREF_RE = /\/(checkout|checkouts)(\/|\?|#|$)/i;
  // var checkoutGateBusy = false;

  // function looksLikeCheckoutTrigger(el) {
  //   if (!el || el.nodeType !== 1 || !el.matches) return false;
  //   if (el.matches('a[href]') && CHECKOUT_HREF_RE.test(el.getAttribute('href') || '')) return true;
  //   if (!/^(button|a)$/i.test(el.tagName) && el.getAttribute('role') !== 'button') return false;
  //   var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
  //   return text.length < 40 && /checkout/i.test(text);
  // }

  // function proceedToCheckout(trigger) {
  //   if (trigger.tagName === 'A' && trigger.href) {
  //     window.location.href = trigger.href;
  //   } else if (typeof trigger.click === 'function') {
  //     trigger.click(); /* checkoutGateBusy is still true, so this re-entrant
  //                          click is ignored by the listener below */
  //   }
  // }

  // document.addEventListener('click', function (event) {
  //   if (!THRESHOLD || checkoutGateBusy) return;

  //   var path = event.composedPath ? event.composedPath() : [event.target];
  //   var trigger = null;
  //   for (var i = 0; i < path.length; i++) {
  //     if (looksLikeCheckoutTrigger(path[i])) { trigger = path[i]; break; }
  //   }
  //   if (!trigger) return;

  //   checkoutGateBusy = true;
  //   event.preventDefault();
  //   event.stopPropagation();
  //   if (event.stopImmediatePropagation) event.stopImmediatePropagation();

  //   getCart()
  //     .then(function (cart) {
  //       var lines = limitedLines(cart);
  //       if (lines.length && !qualifies(cart)) {
  //         revokeLimited(lines);
  //         notify(CFG.checkoutBlockedMessage ||
  //           ('Your ₹1 gift needs ' + money(THRESHOLD - otherTotal(cart)) +
  //            ' more in cart — it\u2019s been removed. Add more and try checkout again.'));
  //         return;
  //       }
  //       proceedToCheckout(trigger);
  //     })
  //     .catch(function () {
  //       proceedToCheckout(trigger); /* fail OPEN — a network blip shouldn't trap a qualifying customer */
  //     })
  //     .then(function () { checkoutGateBusy = false; });
  // }, true);

  /* ---------------- wiring ---------------- */

  document.addEventListener('cart:updated', scheduleState);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleState);
  }

  /*
   * Belt and braces for reservations. The interceptors release on the add's own
   * response, but an add made through a transport we never see (a script that
   * captured window.fetch before this one loaded) would otherwise hold its
   * reservation until the TTL. product-form.js announces both outcomes, so
   * release on those too — a stuck reservation makes a product un-addable,
   * which is a worse failure than the race it prevents.
   */
  function releaseAll() {
    Object.keys(reserved).forEach(release);
  }

  if (typeof subscribe === 'function') {
    try {
      subscribe('cart-error', function (data) {
        var id = data && data.productVariantId;
        if (id) release(id); else releaseAll();
      });
    } catch (e) {}
  }
  document.addEventListener('cart-error', releaseAll);

  /* ---------------- GoKwik panel watch ----------------
   *
   * Quantity edits happen inside GoKwik's cart panel, never reach the theme's
   * pub/sub, and may not go through window.fetch at all — so panel mutations
   * are the only reliable signal that the cart changed. Same approach as
   * assets/cart-unlock-gift-gokwik.js, and scoped to GoKwik's host for the same
   * reason: a document-wide sweep for shadow roots would run on every mutation
   * of a very busy page. If the host is missing, GoKwik simply isn't here.
   */
  var HOST_SELECTOR = CFG.hostSelector || 'shadow-dom-container';
  var MAX_SHADOW_DEPTH = 6;
  var hostCache = null;
  var observedRoots = [];

  function gokwikRoot() {
    if (hostCache && hostCache.isConnected && hostCache.shadowRoot) return hostCache.shadowRoot;
    hostCache = null;
    var host = document.querySelector(HOST_SELECTOR);
    if (host && host.shadowRoot) { hostCache = host; return host.shadowRoot; }
    return null;
  }

  function collectShadowRoots(root, acc, depth) {
    if (!root || depth > MAX_SHADOW_DEPTH) return acc;
    var nodes;
    try { nodes = root.querySelectorAll('*'); } catch (e) { return acc; }
    for (var i = 0; i < nodes.length; i++) {
      var sr = nodes[i].shadowRoot;
      if (sr && acc.indexOf(sr) === -1) {
        acc.push(sr);
        collectShadowRoots(sr, acc, depth + 1);
      }
    }
    return acc;
  }

  function watchGokwikPanel() {
    var root = gokwikRoot();
    if (!root) return;
    collectShadowRoots(root, [root], 0).forEach(function (r) {
      if (observedRoots.indexOf(r) !== -1) return;
      observedRoots.push(r);
      new MutationObserver(scheduleState).observe(r, { childList: true, subtree: true });
    });
  }

  /* Cards re-render on filtering and pagination, so re-apply on DOM changes.
     GoKwik also mounts its host asynchronously — pick it up when it appears. */
  var hostTimer = null;
  new MutationObserver(function () {
    scheduleUi();
    clearTimeout(hostTimer);
    hostTimer = setTimeout(watchGokwikPanel, 200);
  }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  watchGokwikPanel();
  refreshState();

  /* ---------------- debug ---------------- */

  window.ddCollectionLimitDebug = function () {
    return getCart().then(function (cart) {
      console.log('[collection-limit] limited variants:', CFG.variantIds.length);
      console.log('[collection-limit] in cart from collection:', inCart);
      console.log('[collection-limit] limits: maxItems=' + MAX_ITEMS +
                  ' maxQuantity=' + MAX_QTY + ' threshold=' + THRESHOLD);
      console.log('[collection-limit] cards on page:',
                  document.querySelectorAll('.quantity-add-to-cart-btns[data-variant-id]').length);
      console.log('[collection-limit] buttons disabled by us:',
                  document.querySelectorAll('[data-dd-limited="true"]').length);
      console.log('[collection-limit] on a limited product page:', !!CFG.currentProductLimited);
      console.log('[collection-limit] threshold reached:', qualified,
                  qualified ? '' : '(short by ' + money(shortfall) + ')');
      console.log('[collection-limit] limited buttons found on page:', limitedTargets().length);
      console.log('[collection-limit] offer widget:',
                  CFG.showWidget ? (document.getElementById(WIDGET_ID) ? 'rendered' : 'enabled, not rendered')
                                 : 'off (not the offer collection page)');
      return cart;
    });
  };
})();
